<template>
    <div>
        <!-- FILTERS -->
        <div class="row">
            <div class="col-sm-4">
                <!-- FILTER: NAME -->
                <div class="form-group">
                    <label for="filterWebUserName">{{ $t('name') }}</label>
                    <input type="text" id="filterWebUserName" class="form-control" v-model="filterName" />
                </div>
                <!-- FILTER: ROLE -->
                <div class="form-group">
                    <label for="filterWebUserRole">{{ $t('role') }}</label>
                    <select id="filterWebUserRole" class="form-control" v-model="filterRole">
                        <option value="all" selected>{{ $t('all') }}</option>
                        <option v-for="role in rolesWeb" v-bind:key="role" v-bind:value="role">
                            {{ $t(`role_${role}`) }}
                        </option>
                    </select>
                </div>
            </div>
            <div class="col-sm-4">
                <!-- FILTER: E-MAIL -->
                <div class="form-group">
                    <label for="filterWebUserEmail">{{ $t('email') }}</label>
                    <input type="text" id="filterWebUserEmail" class="form-control" v-model="filterEmail" />
                </div>
                <!-- FILTER: STATUS -->
                <div class="form-group">
                    <label for="filterWebUserStatus">{{ $t('status') }}</label>
                    <select class="form-control" id="filterWebUserStatus" v-model="filterStatus">
                        <option v-for="status in statusValues" :key="status" :value="status">
                            {{ $t(status) }}
                        </option>
                    </select>
                </div>
            </div>
            <!-- FILTER: PHARMACY (SHOWN IF CURRENT USER IS SUPERADMIN AND PHARMACYWEBUSERS PARAM NOT SET) -->
            <div class="col-sm-4" v-if="user.permissions.superadmin && pharmacyWebUsers === null">
                <div class="form-group">
                    <label for="filterPharmacy">{{ $t('pharmacy') }}</label>
                    <select id="filterPharmacy" class="form-control" v-model="filterPharmacy">
                        <option value="all" selected>{{ $t('all') }}</option>
                        <option v-for="pharmacy of pharmacies" v-bind:key="pharmacy" v-bind:value="pharmacy">
                            {{ pharmacy }}
                        </option>
                    </select>
                </div>
            </div>
        </div>
        <!-- TABLE -->
        <div class="mt-4">
            <b-table striped hover :fields="tableFields" :items="filteredUsers">
                <template #cell(pharmacy)="data">
                    {{ data.item.pharmacy_id ? data.item.pharmacy.name : '–' }}
                </template>
                <template #cell(role)="data">
                    {{ $t(`role_${data.item.role}`) }}
                </template>
                <template #cell(status)="data">
                    {{ $t(data.item.status) }}
                    <span style="color:#b13535" v-if="user.permissions.superadmin && data.item.incorrect_logins >= 3"><br>[{{ $t('many_incorrect_logins') }}]</span>
                </template>
                <template #cell(created_at)="data">
                    {{ getLocalDate(data.item.created_at, $i18n.locale, true, true) }}
                </template>
                <template #cell(login_date)="data">
                    {{ getLocalDate(data.item.login_date, $i18n.locale, true, true) }}
                </template>
                <!-- BUTTON: EDIT USER -->
                <template #cell(button_edit)="data">
                    <button
                        class="btn btn-sm btn-info"
                        v-on:click="$router.push({ name: 'webUser.edit', params: { id: data.item.id } })"
                        :disabled="submitClicked"
                    >
                        {{ $t('edit') }}
                    </button>
                </template>
                <!-- BUTTON: DELETE USER -->
                <template #cell(button_delete)="data">
                    <button
                        class="btn btn-sm btn-danger"
                        v-on:click="() => handleDeleteWebUserClick(data.item.id, data.item.pharmacy_id)"
                        :disabled="submitClicked"
                    >
                        {{ $t('delete') }}
                    </button>
                </template>
            </b-table>
        </div>
        <!-- MODAL: ARE YOU SURE YOU WANT TO DELETE WEB USER? -->
        <b-modal id="deleteWebUserModal" centered :title="deleteWebUserModalText.title" hide-footer>
            <div class="row">
                <div class="col" v-html="deleteWebUserModalText.body"></div>
            </div>
            <div class="d-flex row mt-2">
                <div class="d-flex col justify-content-center">
                    <button class="btn btn-primary mr-4" @click="deleteWebUser">
                        {{ $t('ok') }}
                    </button>
                    <button class="btn btn-secondary" @click="handleDeleteWebUserCancel">
                        {{ $t('cancel') }}
                    </button>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import {
    deleteWebUser,
    getPharmacyConfig,
    getRoles,
    getWebUsers,
    getWebUsersForPharmacy,
    handleAxiosError
} from '@/services/axiosService';
import { getLocalDate } from '@/services/dateService';

export default {
    props: {
        // if pharmacyWebUsers is set, we only show a single pharmacy's web users
        pharmacyWebUsers: {
            validator: prop => typeof prop === 'object' || prop === null,
            required: false,
            default: () => null
        }
    },
    data() {
        return {
            deleteWebUserId: null,
            deleteWebUserModalText: {
                title: '',
                body: ''
            },
            error: false,
            filterEmail: '',
            filterName: '',
            filterPharmacy: 'all',
            filterRole: 'all',
            filterStatus: 'all',
            getLocalDate,
            pharmacies: [],
            rolesWeb: [],
            statusValues: ['all', 'active', 'inactive'],
            submitClicked: false,
            webUsers: []
        };
    },
    async created() {
        // get web users and web user roles
        await this.getWebUsers();
        if (!this.error) {
            await this.getRoles();
        }
        if (!this.error) {
            // set component loaded
            this.$emit('loaded');
        }
    },
    computed: {
        ...mapState('account', ['user']),
        filteredUsers() {
            return this.webUsers.filter(item => {
                return (
                    ((item.first_name !== null &&
                        item.first_name.toLowerCase().indexOf(this.filterName.toLowerCase()) > -1) ||
                        (item.last_name !== null &&
                            item.last_name.toLowerCase().indexOf(this.filterName.toLowerCase()) > -1) ||
                        (this.filterName === '' && item.last_name === null && item.first_name === null)) &&
                    (!item.email || (item.email && item.email.indexOf(this.filterEmail.toLowerCase()) > -1)) &&
                    (!item.status || this.filterStatus === 'all' || item.status === this.filterStatus) &&
                    (this.filterRole === 'all' || item.role === this.filterRole) &&
                    (this.filterPharmacy === 'all' || (item.pharmacy_id && item.pharmacy.name === this.filterPharmacy))
                );
            });
        },
        tableFields() {
            let tableFields = [
                { key: 'first_name', label: this.$t('first_name'), sortable: true, tdClass: 'align-middle' },
                { key: 'last_name', label: this.$t('last_name'), sortable: true, tdClass: 'align-middle' },
                { key: 'email', label: this.$t('email'), sortable: true, tdClass: 'align-middle' }
            ];
            if (this.user.permissions.superadmin && this.pharmacyWebUsers === null) {
                tableFields.push({
                    key: 'pharmacy',
                    label: this.$t('pharmacy'),
                    sortable: true,
                    tdClass: 'align-middle'
                });
            }
            tableFields = [
                ...tableFields,
                { key: 'role', label: this.$t('role'), sortable: true, tdClass: 'align-middle' },
                { key: 'status', label: this.$t('status'), sortable: true, tdClass: 'align-middle' },
                { key: 'created_at', label: this.$t('created_at'), sortable: true, tdClass: 'align-middle' },
                { key: 'login_date', label: this.$t('last_login_date'), sortable: true, tdClass: 'align-middle' }
            ];
            if (this.user.permissions.edit_user) {
                tableFields = [
                    ...tableFields,
                    { key: 'button_edit', sortable: false, label: '', tdClass: 'align-middle' },
                    { key: 'button_delete', sortable: false, label: '', tdClass: 'align-middle' }
                ];
            }
            return tableFields;
        }
    },
    methods: {
        ...mapActions('account', ['logout']),
        // delete web user (via modal)
        async deleteWebUser() {
            try {
                this.$bvModal.hide('deleteWebUserModal');
                await deleteWebUser(this.deleteWebUserId);
                this.$toastr.s(this.$t('web_user_deleted'));
                if (this.deleteWebUserId === this.user.id) {
                    // if user has deleted themselves, log them out
                    this.logout();
                    return;
                } else {
                    // otherwise remove deleted user from users
                    this.webUsers = this.webUsers.filter(user => {
                        return user.id !== this.deleteWebUserId;
                    });
                }
            } catch (error) {
                handleAxiosError(error);
            }
            this.deleteWebUserId = null;
            this.submitClicked = false;
        },
        // get web user roles
        async getRoles() {
            try {
                const roles = await getRoles('web');
                // only superadmin can set target user's role to "superadmin"
                roles.map(role => {
                    if (this.user.permissions.superadmin || role.slug !== 'superadmin') {
                        if (role.web_role) {
                            this.rolesWeb.push(role.slug);
                        }
                    }
                });
            } catch (error) {
                handleAxiosError(error);
                this.error = true;
            }
        },
        // get webtool users for pharmacy
        async getWebUsers() {
            try {
                let webUsers;
                if (this.pharmacyWebUsers === null) {
                    // if pharmacyWebUsers not set, get users
                    if (this.user.permissions.superadmin) {
                        // if current user is superadmin, get all web users
                        webUsers = await getWebUsers();
                        const pharmacies = [
                            ...new Set(
                                webUsers.reduce((acc, user) => {
                                    if (user.pharmacy_id) {
                                        acc.push(user.pharmacy.name);
                                    }
                                    return acc;
                                }, [])
                            )
                        ];
                        this.pharmacies = _.sortBy(pharmacies, [pharmacy => pharmacy.toLowerCase()]);
                    } else {
                        // if current user is not superadmin, get pharmacy's web users
                        webUsers = await getWebUsersForPharmacy(this.user.pharmacy_id);
                    }
                } else {
                    // if pharmacyWebUsers set, use it (don't retrieve from backend)
                    webUsers = this.pharmacyWebUsers;
                }
                // sort web users by pharmacy name, last name and first name (case insensitive)
                this.webUsers = _.orderBy(webUsers, [
                    user => (user.pharmacy ? user.pharmacy.name.toLowerCase() : user.pharmacy_id),
                    user => user.last_name.toLowerCase(),
                    user => user.first_name.toLowerCase()
                ]);
            } catch (error) {
                handleAxiosError(error);
                this.error = true;
            }
        },
        // handle "delete web user" cancel
        handleDeleteWebUserCancel() {
            this.submitClicked = false;
            this.deleteWebUserId = null;
            this.$bvModal.hide('deleteWebUserModal'); // hide confirmation modal
        },
        // handle "delete web user" click
        async handleDeleteWebUserClick(webUserId, pharmacyId) {
            this.submitClicked = true;
            this.deleteWebUserId = webUserId;
            // set confirmation modal text (use special text if user is deleting themselves)
            if (webUserId === this.user.id) {
                this.deleteWebUserModalText.title = this.$t('delete_own_web_user_are_you_sure_title');
                this.deleteWebUserModalText.body = `<p>${this.$t('delete_own_web_user_are_you_sure')}</p>`;
            } else {
                this.deleteWebUserModalText.title = this.$t('delete_web_user_are_you_sure_title');
                this.deleteWebUserModalText.body = `<p>${this.$t('delete_web_user_are_you_sure')}</p>`;
            }
            if (pharmacyId) {
                // if user to be deleted belongs to a pharmacy, check if they're set as alert recipient
                // and add prompt to confirmation modal if needed
                const pharmacyConfig = await getPharmacyConfig(pharmacyId);
                const expiryAlertRecipient =
                    pharmacyConfig.expiry_alert && pharmacyConfig.expiry_alert.recipient === webUserId;
                const lowInventoryAlertRecipient =
                    pharmacyConfig.low_inventory_alert && pharmacyConfig.low_inventory_alert.recipient === webUserId;
                if (expiryAlertRecipient || lowInventoryAlertRecipient) {
                    this.deleteWebUserModalText.body =
                        this.deleteWebUserModalText.body +
                        `<p>${this.$t('delete_web_user_alert_recipient_prompt_1')}` +
                        `<ul style="margin-top: 10px; margin-bottom: 10px;">` +
                        `${expiryAlertRecipient ? `<li>${this.$t('expiry_alerts')}</li>` : ''}` +
                        `${lowInventoryAlertRecipient ? `<li>${this.$t('low_inventory_alerts')}</li>` : ''}` +
                        `</ul>${this.$t('delete_web_user_alert_recipient_prompt_2')}</p>`;
                }
            }
            this.$bvModal.show('deleteWebUserModal'); // show confirmation modal
        }
    },
    // handle other "hide modal" actions (background click, esc key press, modal close click)
    mounted() {
        this.$root.$on('bv::modal::hide', bvEvent => {
            if (['backdrop', 'esc', 'headerclose'].includes(bvEvent.trigger)) {
                this.deleteWebUserId = null;
                this.submitClicked = false;
            }
        });
    }
};
</script>
